import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FiCheck, FiMail, FiPackage, FiTruck, FiCreditCard, FiHome, FiShoppingBag } from 'react-icons/fi';
import { clearCart } from '../utils/cart';
import { useCart } from '../contexts/CartContext';

interface CustomerDetails {
  name?: string;
  email?: string;
  phone?: string;
  shipping?: {
    name: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
  billing?: {
    name: string;
    email?: string;
    phone?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
  };
}

interface PaymentDetails {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  line_items?: Array<{
    description: string;
    quantity: number;
    amount: number;
    currency: string;
  }>;
}

interface OrderDetails {
  success: boolean;
  customer: CustomerDetails;
  payment: PaymentDetails;
  timestamp: string;
}

// Generate a realistic order number
const generateOrderNumber = (): string => {
  const prefix = 'GR804';
  const timestamp = Date.now().toString().slice(-6);
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
};

// Clean up old processed order IDs from localStorage (older than 24 hours)
const cleanupProcessedOrders = () => {
  const now = Date.now();
  const oneDayAgo = now - (24 * 60 * 60 * 1000); // 24 hours in milliseconds
  
  // Get all localStorage keys
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith('order_processed_')) {
      const storedData = localStorage.getItem(key);
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          if (data.timestamp && data.timestamp < oneDayAgo) {
            localStorage.removeItem(key);
            console.log(`Cleaned up old processed order: ${key}`);
          }
        } catch (e) {
          // If it's not JSON (old format), remove it
          localStorage.removeItem(key);
        }
      }
    }
  }
};

// Store order in database
const storeOrderInDatabase = async (orderData: OrderDetails, paymentIntentId?: string, sessionId?: string): Promise<{success: boolean, message?: string}> => {
  try {
    // SECURITY: Only send session/payment intent ID to server
    // Server will fetch all order data from Stripe (authoritative source)
    const orderPayload: { sessionId?: string; paymentIntentId?: string } = {};
    
    if (sessionId) {
      orderPayload.sessionId = sessionId;
    } else if (paymentIntentId) {
      orderPayload.paymentIntentId = paymentIntentId;
    } else {
      console.error('No session_id or payment_intent_id available');
      return { success: false };
    }

    console.log('Creating order with Stripe data only:', orderPayload);

    const response = await fetch('/api/create-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderPayload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to store order:', errorData);
      return { success: false };
    } else {
      const result = await response.json();
      console.log('Order stored successfully:', result);
      return { success: true, message: result.message };
    }
  } catch (error) {
    console.error('Error storing order:', error);
    // Don't fail the success page if order storage fails
    return { success: false };
  }
};

const CheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderNumber] = useState(generateOrderNumber());
  const [hasProcessed, setHasProcessed] = useState(false);
  const { clearCart: clearCartContext } = useCart();

  useEffect(() => {
    // Clean up old processed orders first
    cleanupProcessedOrders();
    
    // Get session ID or payment intent ID
    const paymentIntentId = searchParams.get('payment_intent');
    const sessionId = searchParams.get('session_id');
    const identifier = sessionId || paymentIntentId;
    
    const fetchOrderDetailsOnly = async () => {
      if (!paymentIntentId && !sessionId) {
        setError('No payment information found');
        setLoading(false);
        return;
      }

      try {
        let endpoint = '/api/get-payment-details';
        let params = paymentIntentId 
          ? `?payment_intent_id=${paymentIntentId}`
          : `?session_id=${sessionId}`;

        const response = await fetch(endpoint + params);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch order details');
        }

        setOrderDetails(data);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    const fetchOrderDetails = async () => {
      if (!paymentIntentId && !sessionId) {
        setError('No payment information found');
        setLoading(false);
        return;
      }

      try {
        let endpoint = '/api/get-payment-details';
        let params = paymentIntentId 
          ? `?payment_intent_id=${paymentIntentId}`
          : `?session_id=${sessionId}`;

        const response = await fetch(endpoint + params);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch order details');
        }

        setOrderDetails(data);
        
        // Store the order in our database (API will prevent duplicates)
        const orderResponse = await storeOrderInDatabase(data, paymentIntentId || undefined, sessionId || undefined);
        
        // Mark as processed in localStorage to prevent re-processing on refresh
        if (identifier) {
          localStorage.setItem(`order_processed_${identifier}`, JSON.stringify({
            timestamp: Date.now(),
            processed: true
          }));
        }
        
        // Clear the cart after successful order processing (only if this is the first processing)
        if (!orderResponse?.message?.includes('already processed')) {
          clearCartContext();
          
          // Dispatch a custom event to trigger product refresh across the app
          // This ensures that product lists will update to show sold items are no longer available
          const refreshEvent = new CustomEvent('productInventoryChanged', {
            detail: { 
              reason: 'checkout_success',
              orderDetails: data 
            }
          });
          window.dispatchEvent(refreshEvent);
        }
        
        // Mark as processed to prevent re-execution
        setHasProcessed(true);
        
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    // Check if this order has already been processed in this browser session
    if (identifier) {
      const processedData = localStorage.getItem(`order_processed_${identifier}`);
      if (processedData) {
        try {
          const data = JSON.parse(processedData);
          const oneHourAgo = Date.now() - (60 * 60 * 1000); // 1 hour in milliseconds
          
          // Only skip if processed within the last hour (prevents issues with old stale data)
          if (data.timestamp && data.timestamp > oneHourAgo) {
            console.log('Order already processed recently, skipping re-processing');
            setHasProcessed(true);
            // Still fetch order details for display, but skip processing
            fetchOrderDetailsOnly();
            return;
          } else {
            // Remove stale data
            localStorage.removeItem(`order_processed_${identifier}`);
          }
        } catch (e) {
          // Remove invalid data
          localStorage.removeItem(`order_processed_${identifier}`);
        }
      }
    }
    
    if (hasProcessed) return;

    fetchOrderDetails();
  }, [searchParams, hasProcessed]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-primary">Processing your order...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-dark flex items-center justify-center">
        <div className="max-w-md mx-auto p-8 text-center">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 mb-6">
            <div className="text-red-400 text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-primary mb-2">Order Verification Failed</h1>
            <p className="text-secondary">{error}</p>
          </div>
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-accent text-white font-medium rounded-lg hover:bg-accent/90 transition-colors"
          >
            <FiHome className="mr-2" />
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-dark">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-80 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-80 w-80 h-80 bg-accent/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img 
              src="/images/grailed804logo.webp" 
              alt="GRAILED804 Logo" 
              className="h-16 w-16 rounded-full object-cover border-2 border-accent/30 mx-auto hover:border-accent/60 transition-colors"
            />
          </Link>
          <div className="text-accent text-sm font-medium tracking-wider uppercase">
            GRAILED804
          </div>
        </div>

        {/* Success Animation & Message */}
        <div className="max-w-2xl mx-auto">
          {/* Success Icon Animation */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 border-2 border-green-500/30 rounded-full mb-6 animate-pulse">
              <FiCheck className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              Order Confirmed!
            </h1>
            <p className="text-secondary text-lg">
              Thank you for your purchase. Your order has been successfully processed.
            </p>
          </div>

          {/* Order Summary Card */}
          <div className="bg-background-light border border-gray-800 rounded-xl p-6 mb-6 backdrop-blur-sm bg-background-light/50">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-primary mb-1">Order Details</h2>
                <p className="text-accent font-mono text-sm">{orderNumber}</p>
              </div>
                             <div className="text-right">
                 <p className="text-2xl font-bold text-primary">
                   ${orderDetails ? (orderDetails.payment.amount / 100).toFixed(2) : '0.00'}
                 </p>
                 <p className="text-secondary text-sm">
                   {orderDetails?.payment?.currency?.toUpperCase() || 'USD'}
                 </p>
               </div>
            </div>

            {/* Customer Information */}
                         {orderDetails?.customer && (
               <div className="grid md:grid-cols-2 gap-6 mb-6">
                 <div>
                   <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">
                     Customer Information
                   </h3>
                   <div className="space-y-2">
                     <p className="text-primary font-medium">{orderDetails.customer.name || orderDetails.customer.shipping?.name || 'Customer'}</p>
                     <p className="text-secondary flex items-center">
                       <FiMail className="w-4 h-4 mr-2" />
                       {orderDetails.customer.email || orderDetails.customer.billing?.email || 'No email provided'}
                     </p>
                     {(orderDetails.customer.phone || orderDetails.customer.billing?.phone) && (
                       <p className="text-secondary">{orderDetails.customer.phone || orderDetails.customer.billing?.phone}</p>
                     )}
                   </div>
                 </div>

                 {orderDetails.customer.shipping && (
                   <div>
                     <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">
                       Shipping Address
                     </h3>
                     <div className="text-secondary space-y-1">
                       <p>{orderDetails.customer.shipping.address.line1}</p>
                       {orderDetails.customer.shipping.address.line2 && (
                         <p>{orderDetails.customer.shipping.address.line2}</p>
                       )}
                       <p>
                         {orderDetails.customer.shipping.address.city}, {orderDetails.customer.shipping.address.state} {orderDetails.customer.shipping.address.postal_code}
                       </p>
                       <p>{orderDetails.customer.shipping.address.country}</p>
                     </div>
                   </div>
                 )}
               </div>
             )}

                         {/* Order Items */}
             {orderDetails?.payment?.line_items && orderDetails.payment.line_items.length > 0 && (
               <div className="mb-6">
                 <h3 className="text-sm font-medium text-accent mb-3 uppercase tracking-wider">
                   Items Ordered
                 </h3>
                 <div className="space-y-3">
                   {orderDetails.payment.line_items.map((item, index) => (
                     <div key={index} className="flex justify-between items-center py-2 border-b border-gray-800/50 last:border-b-0">
                       <div>
                         <p className="text-primary font-medium">{item.description}</p>
                         <p className="text-secondary text-sm">Quantity: {item.quantity}</p>
                       </div>
                       <p className="text-primary font-medium">
                         ${(item.amount / 100).toFixed(2)}
                       </p>
                     </div>
                   ))}
                 </div>
               </div>
             )}
          </div>

          {/* Email Notification Info */}
          <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                  <FiMail className="w-5 h-5 text-accent" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-primary font-medium mb-2">Order Confirmation Email</h3>
                <p className="text-secondary text-sm leading-relaxed">
                  A detailed order confirmation has been sent to <strong className="text-primary">{orderDetails?.customer?.email || orderDetails?.customer?.billing?.email || 'your email'}</strong>. 
                  You'll receive email updates when your order status changes from "New Order" to "Completed" and when it's "Shipped".
                </p>
              </div>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-background-light/30 border border-gray-800/50 rounded-lg p-4 text-center hover:bg-background-light/40 transition-colors">
              <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <FiCreditCard className="w-4 h-4 text-accent" />
              </div>
              <h4 className="text-primary font-medium text-sm mb-1">Payment Processed</h4>
              <p className="text-secondary text-xs">Your payment has been securely processed</p>
            </div>

            <div className="bg-background-light/30 border border-gray-800/50 rounded-lg p-4 text-center hover:bg-background-light/40 transition-colors">
              <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <FiPackage className="w-4 h-4 text-accent" />
              </div>
              <h4 className="text-primary font-medium text-sm mb-1">Order Received</h4>
              <p className="text-secondary text-xs">We're preparing your items for shipment</p>
            </div>

            <div className="bg-background-light/30 border border-gray-800/50 rounded-lg p-4 text-center hover:bg-background-light/40 transition-colors">
              <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <FiTruck className="w-4 h-4 text-accent" />
              </div>
              <h4 className="text-primary font-medium text-sm mb-1">Shipping Updates</h4>
              <p className="text-secondary text-xs">Track your package via email notifications</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/" 
              className="inline-flex items-center justify-center px-6 py-3 bg-accent text-white font-medium rounded-lg hover:bg-accent/90 transition-all duration-200 transform hover:-translate-y-0.5 hover:shadow-lg"
            >
              <FiHome className="w-4 h-4 mr-2" />
              Continue Shopping
            </Link>
            <Link 
              to="/inventory" 
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-700 text-primary font-medium rounded-lg hover:bg-background-light transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <FiShoppingBag className="w-4 h-4 mr-2" />
              View All Products
            </Link>
          </div>

          {/* Footer Message */}
          <div className="text-center mt-8 pt-8 border-t border-gray-800">
            <p className="text-secondary text-sm">
              Questions about your order? <Link to="/#contact" className="text-accent hover:text-accent/80 underline">Contact our support team</Link>
            </p>
            <p className="text-secondary text-xs mt-2">
              Thank you for choosing GRAILED804 for all your premium sneaker needs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess; 